import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import Image from '../../components/Image';
import AppSidebar from './AppSidebar';

/**
 * Portal target for route-specific controls on the right of the mobile strip.
 *
 * The strip lives in AppNavigation and therefore renders on every route, while
 * controls like the packet export menu depend on providers that only exist inside
 * their own route — usePacketContext throws outside a PacketProvider. Portalling in
 * from the route means such a control can only ever mount where its providers do.
 */
export const MOBILE_HEADER_ACTIONS_ID = 'mobile-header-actions';

export default function MobileNavDrawer() {
  const [isOpen, setIsOpen] = useState(false);

  // Close on any route change. The drawer is layout, not route content — it is a
  // sibling of the <Outlet /> — so a client-side navigation from inside it leaves
  // it sitting over the page it just navigated. `onNavItemClick` only covers the
  // SidebarItems it is threaded to, which is why every other link in here has to
  // be a plain anchor whose full page load tears the drawer down.
  //
  // Keyed on location.key rather than pathname so navigating to the SAME path
  // with different search params still closes it — which is exactly what the
  // sidebar ads' prompt CTA does.
  const { key: locationKey } = useLocation();
  useEffect(() => setIsOpen(false), [locationKey]);

  return (
    <>
      {/* Mobile top strip - visible only below md */}
      {/* data-allow-click-when-logged-out sits on the nav affordances individually, NOT on
          this container. ProjectClickCaptures (project.jsx) intercepts every click for
          signed-out users with a capture-phase document listener, exempting anything inside
          that attribute — via closest(), which walks the REAL DOM. A portal moves the real
          node, so an attribute here would exempt whatever a route portals into the slot
          below, letting signed-out users drive controls that are click-gated everywhere
          else. */}
      <div className='flex md:hidden items-center justify-between px-4 h-14 bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-30 print:hidden'>
        <div className='flex items-center gap-1'>
          <button
            // Always "Open": this button only ever opens the drawer, and while the drawer is open
            // the Dialog covers it and carries its own close button.
            data-allow-click-when-logged-out
            aria-label='Open navigation menu'
            onClick={() => setIsOpen(true)}
            className='-ml-2 p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100'>
            <Bars3Icon className='h-6 w-6' aria-hidden='true' />
          </button>
          <a data-allow-click-when-logged-out href='/'>
            <Image className='h-9 w-auto' src='/logo.svg' alt='Diffit' />
          </a>
        </div>
        {/* Filled by whichever route is mounted — see MOBILE_HEADER_ACTIONS_ID. Deliberately
            NOT exempt from the logged-out click gate: a portalled control should behave the
            same as its desktop counterpart, which prompts sign-in. */}
        <div id={MOBILE_HEADER_ACTIONS_ID} className='flex items-center' />
      </div>
      {/* Spacer for the fixed mobile strip */}
      <div className='md:hidden h-14 flex-shrink-0' />

      {/* Slide-in drawer */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog onClose={() => setIsOpen(false)} className='relative z-50'>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter='transition-opacity duration-200'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity duration-150'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'>
            <div className='fixed inset-0 bg-black/30' aria-hidden='true' />
          </Transition.Child>

          {/* Drawer panel */}
          <Transition.Child
            as={Fragment}
            enter='transition-transform duration-200'
            enterFrom='-translate-x-full'
            enterTo='translate-x-0'
            leave='transition-transform duration-150'
            leaveFrom='translate-x-0'
            leaveTo='-translate-x-full'>
            <Dialog.Panel className='fixed inset-y-0 left-0 w-60 bg-white shadow-xl'>
              <div className='absolute top-3 right-3 z-10'>
                <button
                  aria-label='Close navigation menu'
                  onClick={() => setIsOpen(false)}
                  className='p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100'>
                  <XMarkIcon className='h-5 w-5' />
                </button>
              </div>
              <AppSidebar forceExpanded onNavItemClick={() => setIsOpen(false)} />
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
}
